import React, { useCallback, useEffect, useRef, useState } from 'react';
import showToast from '../../utils/toast';
import { Location } from '../../models/LocationModel';
import LocationForm from '../../components/Locations/LocationForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import useLocationsService from '../../services/useLocationsService';
import LocationsTable from './LocationsTable';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination from '../../components/Base/Pagination';
import TableTextSearch from '../../components/Base/TableTextSearch';
import AuditInfoModal from '../../components/Base/AuditInfoModal';
import MaintainerPageShell from '../../components/Base/MaintainerPageShell';
import { MapPin, Plus } from 'lucide-react';
import { finalizeTextInput } from '../../utils/textInputSanitize';

const LocationsPage = () => {

  
  const svc = useLocationsService();
  const { list: getLocations, store: createLocation, update: updateLocation, remove: removeLocation, fetching } = svc;
  const [locations, setLocations] = useState<Location[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [editing, setEditing] = useState<Location | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const activeRequest = useRef<AbortController | null>(null);
 const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [companyIdToDelete, setLocationIdToDelete] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  
  
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const load = useCallback((page: number = 1, search?: string) => {
    console.log('🔄 LocationsPage - Iniciando carga de localizaciones');
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    getLocations(
      filters as Record<string, string | number>,
      {
        onSuccess: (resp) => {
          console.log('✅ LocationsPage - Localizaciones cargadas exitosamente', resp);
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.locations) ? data.locations : data.items ?? [];
          setLocations(items);
          
          // Actualizar información de paginación
          if (data.pagination) {
            setPagination({
              page: data.pagination.page || page,
              totalPages: data.pagination.total_pages || 1,
              totalCount: data.pagination.total_count || 0,
              pageSize: data.pagination.page_size || 10,
              hasNext: data.pagination.has_next || false,
              hasPrevious: data.pagination.has_previous || false,
            });
          }
        },
        onError: (resp) => {
          console.log('❌ LocationsPage - Error al cargar localizaciones', resp);
          console.log('📊 Status HTTP:', resp?.http_status);
          if (resp?.http_status === 403) {
            showToast(
              resp?.message || 'No tienes permisos para acceder a esta sección.',
              'error'
            );
          } else {
            showToast(resp?.message ?? 'error');
          }
        },
      },
      ctrl.signal
    );
  }, [getLocations]);




  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [load]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1, value), 400);
    },
    [load]
  );



  const openDeleteConfirmation = (id: number) => {
    setLocationIdToDelete(id);
    setIsAlertOpen(true);
  };
  const openCreate = () => {
    setEditing({ name: '' });
    setAuditModalOpen(false);
    setShowForm(true);
  };
  const openEdit = (c: Location) => {
    setEditing(c);
    setFieldError(null);
    setAuditModalOpen(false);
    setShowForm(true);
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
    setAuditModalOpen(false);
  };

  const callbackSave = async (): Promise<any> => {
    console.log('[companies] callbackSave start', { editing });
    if (!editing) {
      console.log('[companies] no editing, abort');
      return;
    }

    clearErrors();
    setSavingLocked(true);
    try {
      const name = finalizeTextInput(editing.name ?? '');
      if (name !== editing.name) {
        setEditing((prev) => (prev ? { ...prev, name } : prev));
      }
      if (editing.id) {
        clearErrors();
        const r = await updateLocation(editing.id, { name });
        return r;
      } else {
        const r = await createLocation({ name });
        return r;
      }
      } catch (error: any) {

        // ---- Mapea correctamente error.errors.items[0].name ----
        const nameErr =
          error?.errors?.items?.[0]?.name?.[0] ??
          error?.errors?.items?.["0"]?.name?.[0];

        if (nameErr) {
          setErrorsFromResponse({ name: [nameErr] });
        }

        throw error;
    } finally {
      setSavingLocked(false);
      console.log('[companies] callbackSave end');
    }
  };
  const handleSaveComplete = (ok: boolean, resp?: any, errors?: any,) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      const saved = resp?.data;
      if (saved?.id && editing?.id) {
        setEditing((prev) => (prev ? { ...prev, ...saved } : prev));
      }
      setShowForm(false);
      setEditing(null);
      setAuditModalOpen(false);
      clearErrors();
      load(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }
    // if (!ok) {
    //         // 1. Establecer errores de campo si existen
    //         setErrorsFromResponse(resp); 

    //         // 2. Determinar el mensaje a mostrar, priorizando 'message' del ApiResponse
    //         const toastMessage = resp?.message || Object.values(resp || errors || {})[0] || 'Error desconocido al guardar.';
            
    //         // 3. Mostrar el toast con el mensaje correcto
    //         showToast(toastMessage, 'error');
    //     }
    // Establecer todos los errores de campos
    setErrorsFromResponse(resp);
    

  };

  // const handleDelete = (id: number) => {
  //   // usar window.confirm para evitar la regla de eslint no-restricted-globals
  //   if (!window.confirm('¿Eliminar empresa?')) return;
  //   removeLocation(id, {
  //     onSuccess: (r: any) => {
  //       showToast(r?.message ?? 'Eliminado', 'success');
  //       load();
  //     },
  //     onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
  //   });
  // };

  const handleConfirmDelete = () => {
    if (companyIdToDelete === null) return;

    removeLocation(companyIdToDelete, {
      onSuccess: (r: any) => {
        showToast(r?.message ?? 'Eliminado', 'success');
        load(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: (r: any) => showToast(r?.message ?? 'Error eliminando', 'error'),
    });

    setIsAlertOpen(false);       // ← cerrar alerta
    setLocationIdToDelete(null);  // limpiar id
  };
  const handleCloseAlert = () => {
    setIsAlertOpen(false);
    setLocationIdToDelete(null); // Opcional: limpiar el id al cancelar
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || locations.length}
        </span>
        ubicaciones
      </span>
    );

  return (
    <MaintainerPageShell
      title="Ubicaciones"
      icon={<MapPin size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nueva ubicación
        </button>
      }
    >
      <LocationsTable
        data={locations}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={pagination.totalCount}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre de ubicación..."
            aria-label="Buscar ubicaciones por nombre"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                load(page, searchTextRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      <AuditInfoModal
        isOpen={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
        title="Historial de la ubicación"
        createdAt={editing?.created_at}
        updatedAt={editing?.updated_at}
        createdByName={editing?.created_by_name}
        updatedByName={editing?.updated_by_name}
      />

      {showForm && editing && (
        <div className="companies-modal" role="dialog" aria-modal="true" aria-labelledby="location-form-title">
          <div className="companies-modal-inner">
            <div className="companies-modal-title-row">
              <h3 id="location-form-title">{editing.id ? 'Editar ubicación' : 'Crear ubicación'}</h3>
              {editing.id ? (
                <button
                  type="button"
                  className="btn-audit-info"
                  onClick={() => setAuditModalOpen(true)}
                >
                  Ver historial
                </button>
              ) : null}
            </div>

            <LocationForm value={editing} onChange={setEditing} error={fieldError || undefined} fieldErrors={fieldErrors}/>

            <div className="modal-actions">
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}
      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar ubicación?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={handleCloseAlert}
      />
    </MaintainerPageShell>
  );
};

export default LocationsPage;